import type { IStringTool } from "../core/IStringTool.ts";
import type { ISplitOptionItem } from "../models/text/ISplitOptionItem.ts";
import type { ISplitOptions } from "../models/text/ISplitOptions.ts";
import type { SplitOptionsParserInput } from "../models/text/SplitOptionsParserInput.ts";

export class SplitOptionsParserTool implements IStringTool<SplitOptionsParserInput, ISplitOptions> {
  process({ attributeValue }: SplitOptionsParserInput): ISplitOptions {
    const options: ISplitOptions = {
      segment: "legacy",
      line: [],
      word: [],
      char: [],
      charLine: [],
      charWord: [],
      wordLine: [],
      fit: false,
      trimInlineGaps: false,
    };

    if (!attributeValue) {
      return options;
    }

    const parts = attributeValue.split("|");

    parts.forEach((part) => {
      const trimmedPart = part.trim();
      if (!trimmedPart) return;

      const match = trimmedPart.match(/^([\w-]+)(\[(.*?)\])?$/);

      if (match) {
        const fullOptionKey = this.toCamelCase(match[1]);
        const paramsString = match[3] || "";
        const params = paramsString
          .split(";")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);

        const parsedParam: ISplitOptionItem = this.parseParamsArray(params);

        switch (fullOptionKey) {
          case "segment": {
            const segmentMode = this.parseSegmentMode(paramsString);
            if (segmentMode) {
              options.segment = segmentMode;
            } else {
              console.warn(
                `SplitOptionsParserTool: Unsupported segment mode "${paramsString}" in part "${trimmedPart}"`,
              );
            }
            break;
          }
          case "line":
            (options.line as ISplitOptionItem[]).push(parsedParam);
            break;
          case "word":
            (options.word as ISplitOptionItem[]).push(parsedParam);
            break;
          case "char":
            (options.char as ISplitOptionItem[]).push(parsedParam);
            break;
          case "charLine":
            (options.charLine as ISplitOptionItem[]).push(parsedParam);
            break;
          case "charWord":
            (options.charWord as ISplitOptionItem[]).push(parsedParam);
            break;
          case "wordLine":
            (options.wordLine as ISplitOptionItem[]).push(parsedParam);
            break;
          case "fit":
            options.fit = true;
            break;
          case "trimInlineGaps":
            options.trimInlineGaps = true;
            break;
          default:
            console.warn(
              `SplitOptionsParserTool: Unrecognized option type "${fullOptionKey}" in part "${trimmedPart}"`,
            );
            break;
        }
      } else {
        console.warn(`SplitOptionsParserTool: Could not parse part format "${trimmedPart}"`);
      }
    });

    return options;
  }

  private toCamelCase(str: string): string {
    return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
  }

  private parseParamsArray(params: string[]): ISplitOptionItem {
    const result: ISplitOptionItem = { align: "start" };

    params.forEach((param) => {
      if (param === "abs") {
        result.abs = true;
      } else if (param.startsWith("random")) {
        result.align = "random";
        const randomMatch = param.match(/random\(\s*(-?\d+)\s*,\s*(-?\d+)\s*\)/);
        if (randomMatch) {
          const first = parseInt(randomMatch[1], 10);
          const second = parseInt(randomMatch[2], 10);
          result.random = {
            min: Math.min(first, second),
            max: Math.max(first, second),
          };
        }
      } else if (["start", "center", "end"].includes(param)) {
        result.align = param;
      }
    });

    return result;
  }

  private parseSegmentMode(raw: string): ISplitOptions["segment"] | null {
    const mode = raw.trim().toLowerCase();
    if (mode === "legacy" || mode === "visual") {
      return mode;
    }
    return null;
  }
}
